import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable, DataTableFilterMeta } from 'primereact/datatable';
import { Toolbar } from 'primereact/toolbar';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import customerService from './customer-service';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import tenantService from '../tenant/tenant-service';
import { useTranslation } from 'react-i18next';
import { BreadCrumb } from 'primereact/breadcrumb';
import BreadcrumbComponent from '../../../components/Breadcrumb';
import { Card } from 'primereact/card';

type DialogData = {
    fullName: string;
    [key: string]: any; // Allow additional properties
};

const currentDate = new Date().toLocaleString("en-US", { timeZone: "Asia/Kuala_Lumpur" });

const defaultFilters: DataTableFilterMeta = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    'fullName': {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    representative: { value: null, matchMode: FilterMatchMode.IN },
    date: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }],
    },
    balance: {
        operator: FilterOperator.AND,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    status: {
        operator: FilterOperator.OR,
        constraints: [{ value: null, matchMode: FilterMatchMode.EQUALS }],
    },
    activity: { value: null, matchMode: FilterMatchMode.BETWEEN },
    verified: { value: null, matchMode: FilterMatchMode.EQUALS },
};

const CustomerList: React.FC = () => {
    const [customers, setCustomers] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);
    const [globalFilterValue, setGlobalFilterValue] = useState<string>('');
    const navigate = useNavigate();
    const [clientState, setClientState] = useState<any>(null);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [dialogData, setDialogData] = useState<DialogData | null>(null);
    const { t, i18n } = useTranslation('common');

    const formatDate = (rowData: any) => {
        const date = new Date(rowData.registrationDate);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', }); // Example: dd/mm/yyyy
    };

    useEffect(() => {
        const fetchClientState = async () => {
            try {
                const clientStateItem = await customerService.getClientState();
                setClientState(clientStateItem);
            } catch (error) {
                console.error('Error fetching client state:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchClientState();
    }, []);

    useEffect(() => {
        if (clientState) {
            const fetchUserList = async () => {
                try {
                    const response = await customerService.findAllCust();
                    setCustomers(response); // Assuming the response is the array of user data
                } catch (error) {
                    console.error('Error fetching user data:', error);
                } finally {
                    setLoading(false);
                }
            };

            fetchUserList();
        }
    }, [clientState]);

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const clearFilter = () => {
        initFilters();
    };

    const initFilters = () => {
        setFilters(defaultFilters);
        setGlobalFilterValue('');
    }

    const header = () => {

        function handlePrint(): void {
            window.print();
        };

        function handleCreate(): void {
            navigate(`/admin/customer-edit`);
        }

        return (
            <div className="flex justify-content-between">
                {/* <Button type="button" icon="pi pi-filter-slash" label="Clear" outlined onClick={clearFilter} /> */}
                <div>
                    {/* <InputIcon className="pi pi-search" /> */}
                    <InputText value={globalFilterValue} onChange={onGlobalFilterChange} placeholder={t('keyword-search')} />
                </div>
                <div>
                    <Button label={t('print')} icon="pi pi-print" severity="danger" style={{ marginRight: '.1em' }} onClick={() => handlePrint()} />
                    <Button label={t('new')} icon="pi pi-plus" style={{ marginRight: '.5em' }} onClick={() => handleCreate()} />
                </div>
            </div>
        );
    };

    const actionButton = (rowData: any) => {

        const handleEdit = (cust: any) => {

            navigate('/admin/customer-edit', {
                state: { customer: cust },
            });

        };

        const handleView = (cust: any) => {

            navigate('/admin/customer-view', {
                state: { customer: cust },
            });

        };

        return (
            <div style={{ display: 'flex', gap: '5px' }}>
                <Button
                    label={t('view')}
                    icon="pi pi-eye"
                    className="p-button-info p-button-sm "
                    style={{ padding: '5px 5px', fontSize: '10px' }}
                    onClick={() => handleView(rowData)}
                    tooltip={t('view')}
                    tooltipOptions={{ position: 'top' }}
                />
                <Button
                    label={t('edit')}
                    icon="pi pi-pencil"
                    className="p-button-warning p-button-sm "
                    style={{ padding: '5px 5px', fontSize: '10px' }}
                    onClick={() => handleEdit(rowData)}
                    tooltip={t('edit')}
                    tooltipOptions={{ position: 'top' }}
                />
                {/* Hide "Tenant" button if `systemTenant` is "1" */}
                {rowData.systemTenant !== "1" && (
                    <Button
                        label={t('tenant')}
                        icon="pi pi-plus"
                        className="p-button-secondary p-button-sm"
                        style={{ padding: '5px 5px', fontSize: '10px' }}
                        onClick={() => {
                            setDialogData(rowData)
                            setDialogVisible(true)
                        }}  // Open the dialog
                        tooltip={t('add-tenant')}
                        tooltipOptions={{ position: 'top' }}
                    />
                )}
            </div>
        )

    };

    async function handleDialogSave(): Promise<void> {

        if (dialogData) {
            // Update customer data
            dialogData.systemTenant = "1";
            const response = await customerService.update(dialogData.customerCode, dialogData);

            // Create tenant
            createTenant(dialogData);


        }
        setDialogVisible(false)
    }

    async function createTenant(dialogData: DialogData) {

        const tenant = {
            facilityCode: dialogData.customerCode,
            fullName: dialogData.fullName,
            shortName: dialogData.fullName,
            instType: 'N',
            autoNoPrefix: dialogData.mnemonic,
            insertBy: clientState.userId,
            insertDate: currentDate,
            modifiedBy: clientState.userId,
            modifiedDate: currentDate,
            recordStatus: '1',
            systemName: 'LIX',
            address: dialogData.address1,
            postcode: dialogData.postcode,
            stateCode: dialogData.stateCode,
            districtCode: dialogData.districtCode,
            countryCode: dialogData.countryCode,
            phone: dialogData.phone,
            fax: dialogData.fax,
            email: dialogData.email
        };

        const response = await tenantService.create(tenant);
    }

    const dialogFooter = (
        <>
            <Button label="Yes" icon="pi pi-check" severity="success" onClick={handleDialogSave} />
            <Button label="No" icon="pi pi-times" severity="danger" onClick={() => setDialogVisible(false)} />
        </>
    );

    const AddTenantDialog = () => {
        return (
            <Dialog
                header="Add New Tenant"
                visible={dialogVisible}
                style={{ width: '50vw' }}
                footer={dialogFooter}
                onHide={() => setDialogVisible(false)}
            >
                {dialogData ? (
                    <div>
                        <p>Are you sure you want to create a tenant for <strong>{dialogData.fullName}</strong>?</p>
                    </div>
                ) : (
                    <p>...</p>
                )}
            </Dialog>
        );
    };

    // const AddTenantDialog = () => (
    //     <Dialog header="Add Tenant" visible={dialogVisible} style={{ width: '30vw' }} footer={dialogFooter} onHide={() => setDialogVisible(false)}>
    //         <p>Are sure you want to create tenant for this customer?</p>
    //         {/* Add your form or inputs for tenant details */}
    //         <p>Details about the selected row: {dialogData}</p>
    //     </Dialog>
    // );

    return (
        <div className='grid'>
            <div className='col-12'>
                <div className='card-title'>
                    <h5>{t('customer')}</h5>
                    <BreadcrumbComponent />
                </div>
                <div className='card'>
                    <>
                        <AddTenantDialog />
                        <DataTable value={customers} showGridlines scrollable scrollHeight='auto' paginator rows={10} rowsPerPageOptions={[5, 10, 25, 50]}
                            filters={filters} globalFilterFields={['fullName', 'facilityCode', 'recordStatus', 'mnemonic']} header={header}
                            sortField='modifiedDate' sortOrder={-1} loading={loading}>
                            <Column
                                header="No."
                                style={{ width: '5%' }}
                                body={(_, { rowIndex }) => rowIndex + 1} // Render row number
                            ></Column>
                            <Column field="registrationDate" header={t('register-date')} body={formatDate}></Column>
                            <Column field="fullName" header={t('customer-name')}></Column>
                            <Column field="customerCode" header={t('customer-id')} ></Column>
                            <Column field="mnemonic" header={t('mnemonic')} ></Column>
                            <Column
                                field="recordStatus"
                                header={t('status')}
                                body={(rowData) => (rowData.recordStatus === "1" ? "Active" : "Inactive")}
                            ></Column>
                            <Column body={actionButton} header={t('action')} style={{ width: '20%' }} ></Column>
                        </DataTable>
                    </>
                </div>
            </div>
        </div>
    );
};

export default CustomerList;


